import React, { useState, useEffect, useMemo } from "react";
import { Label } from "@progress/kendo-react-labels";
import { NumericTextBox } from "@progress/kendo-react-inputs";
import { ComboBox } from "@progress/kendo-react-dropdowns";
import { getClientRefExample, getMatterRefExample } from "api";
import { toast } from "react-toastify";
import styles from "../Settings.module.scss";
import Icon from "components/Global/icon";
import { useNavigate } from "react-router";
import { Button } from "@progress/kendo-react-buttons";
import { updateCompany, useCompany } from "services/companyService";
import SubmitButton from "components/common/SubmitButton";

const selectionData = [
  { name: "None", value: "None" },
  { name: "Client Ref", value: "ClientRef" },
];
const separatorData = [
  { name: "None", value: "N" },
  { name: "/", value: "/" },
  { name: "-", value: "-" },
];

const nameTypeData = [
  { name: "Surname", value: 0 },
  { name: "First Name", value: 1 },
];

function SectionHeading({ sectionName, example }) {
  return (
    <div className="d-flex align-items-center">
      <div className={styles["setup-div"]}>
        <span className={styles["setup-text"]}> {sectionName}</span>
      </div>
      <div className="w-100  border-bottom border-opacity-50"></div>
      <div className={styles["setup-div"]}>
        <span className={styles["setup-text"]}>
          {" "}
          <span className={styles["gray-text"]}>Ref eg (John Smith) : </span>
          {example}
        </span>
      </div>
    </div>
  );
}

const MatterSetup = () => {
  const navigate = useNavigate();
  const [clientRef, setClientRef] = useState("");
  const [matterRef, setMatterRef] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [clientRefParameters, setClientRefParameters] = useState({
    lettersLength: 0,
    numbersLength: 2,
    nameType: { name: "Surname", value: 0 },
  });

  const [matterRefParameters, setMatterRefParameters] = useState({
    selection: { value: "None", name: "None" },
    separator: { value: "None", name: "N" },
    numbersLength: 0,
    initialCheck: false,
  });
  const { data: companyData } = useCompany();

  const initialData = useMemo(() => {
    if (!companyData) return null;
    return {
      matterRefNumbers: companyData?.matterRefNumbers ?? 0,
      matterRefSelection: companyData?.matterRefSelection ?? "None",
      matterRefSeparator: companyData?.matterRefSeparator ?? "N",
      clientRefLetters: companyData?.clientRefLetters ?? 0,
      clientRefNumbers: companyData?.clientRefNumbers ?? 2,
      clientRefNameType: companyData?.clientRefNameType === "FirstName" ? 1 : 0,
    };
  }, [companyData]);

  const currentData = useMemo(
    () => ({
      matterRefNumbers: matterRefParameters?.numbersLength,
      matterRefSelection: matterRefParameters?.selection?.value,
      matterRefSeparator: matterRefParameters?.separator?.value,
      clientRefLetters: clientRefParameters?.lettersLength,
      clientRefNumbers: clientRefParameters?.numbersLength,
      clientRefNameType: clientRefParameters?.nameType?.value,
    }),
    [matterRefParameters, clientRefParameters]
  );

  // Detect if changes have been made
  const hasChanges = useMemo(() => {
    if (!initialData) return false;
    return (
      initialData.matterRefNumbers !== currentData.matterRefNumbers ||
      initialData.matterRefSelection !== currentData.matterRefSelection ||
      initialData.matterRefSeparator !== currentData.matterRefSeparator ||
      initialData.clientRefLetters !== currentData.clientRefLetters ||
      initialData.clientRefNumbers !== currentData.clientRefNumbers ||
      initialData.clientRefNameType !== currentData.clientRefNameType
    );
  }, [initialData, currentData]);

  useEffect(() => {
    if (companyData) {
      setClientRefParameters({
        lettersLength: companyData?.clientRefLetters,
        numbersLength: companyData?.clientRefNumbers,
        nameType: nameTypeData.find(
          (item) => 
            item.value === companyData?.clientRefNameType || 
            item.name === companyData?.clientRefNameType ||
            (companyData?.clientRefNameType === "FirstName" && item.name === "First Name")
        ) || { name: "Surname", value: 0 },
      });
      setMatterRefParameters({
        selection: selectionData.find(
          (item) => item.value === companyData?.matterRefSelection
        ) || { value: "None", name: "None" },
        separator: separatorData.find(
          (item) => item.value === companyData?.matterRefSeparator
        ) || { value: "None", name: "N" },
        numbersLength: companyData?.matterRefNumbers,
        initialCheck: true,
      });
    }
  }, [companyData]);

  useEffect(() => {
    if (matterRefParameters.initialCheck) {
      getClientRef();
      getMatterRef();
    }
  }, [matterRefParameters, clientRefParameters]);

  const submitHandler = async () => {
    const data = {
      matterRefNumbers: matterRefParameters?.numbersLength,
      matterRefSelection: matterRefParameters?.selection?.value,
      matterRefSeparator: matterRefParameters?.separator?.value,
      clientRefLetters: clientRefParameters?.lettersLength,
      clientRefNumbers: clientRefParameters?.numbersLength,
      clientRefNameType: clientRefParameters?.nameType?.value,
    };

    try {
      setIsSubmitting(true);
      await updateCompany(companyData.id, data);
      toast.success("Company Updated");
    } catch (err) {
    } finally {
      setIsSubmitting(false);
    }
  };

  const getClientRef = async () => {
    try {
      const response = await getClientRefExample(
        "John Smith",
        clientRefParameters?.lettersLength,
        clientRefParameters?.numbersLength,
        clientRefParameters?.nameType?.value
      );
      setClientRef(response?.data);
    } catch (error) { }
  };

  const getMatterRef = async () => {
    try {
      const response = await getMatterRefExample(
        "JOH004",
        matterRefParameters?.selection?.value,
        matterRefParameters?.separator?.value,
        matterRefParameters?.numbersLength
      );
      setMatterRef(response?.data);
    } catch (error) { }
  };

  return (
    <div className="d-flex flex-column h-100 w-100">
      <div className="flex-grow-1 overflow-auto">
        <div className="settings-grid-wrapper">
          <div className="mt-3 mb-3">
            <SectionHeading sectionName={"Client Reference"} example={clientRef} />
            <div className="mt-4 ms-5 d-flex align-items-center">
              <div className="ms-5 me-4 d-flex flex-column">
                <Label className="label mb-2">Letters length</Label>
                <NumericTextBox
                  width={100}
                  min={0}
                  max={8}
                  value={clientRefParameters.lettersLength}
                  onChange={(e) => {
                    if (clientRefParameters.numbersLength + e.value <= 10) {
                      setClientRefParameters({
                        ...clientRefParameters,
                        lettersLength: e.value,
                        nameType: e.value > 0 ? clientRefParameters.nameType : { name: "Surname", value: 0 },
                      });
                    } else {
                      toast.error(
                        "Sum of letters and numbers shouldn't be greater than 10"
                      );
                    }
                  }}
                />
              </div>
              <div className="ms-3 me-4 d-flex flex-column">
                <Label className="label mb-2">Name Selection</Label>
                <ComboBox
                  iconClassName="wa"
                  icon={<Icon icon="arrow-down" />}
                  data={nameTypeData}
                  clearButton={false}
                  style={{ width: 120 }}
                  textField={"name"}
                  dataItemKey={"value"}
                  value={clientRefParameters.nameType}
                  disabled={clientRefParameters.lettersLength === 0}
                  onChange={(e) => {
                    setClientRefParameters({
                      ...clientRefParameters,
                      nameType: e.value,
                    });
                  }}
                />
              </div>
              <div className="ms-3 d-flex flex-column">
                <Label className="label mb-2">Numbers length</Label>
                <NumericTextBox
                  width={100}
                  min={2}
                  max={8}
                  value={clientRefParameters.numbersLength}
                  onChange={(e) => {
                    if (clientRefParameters.lettersLength + e.value <= 10) {
                      setClientRefParameters({
                        ...clientRefParameters,
                        numbersLength: e.value,
                      });
                    } else {
                      toast.error(
                        "Sum of letters and numbers shouldn't be greater than 10"
                      );
                    }
                  }}
                />
              </div>
            </div>
          </div>
          <div className="mt-5">
            <SectionHeading sectionName={"Matter Reference"} example={matterRef} />
            <div className="mt-4 ms-5 d-flex align-items-center">
              <div className="ms-5 d-flex flex-column">
                <Label className="label mb-2">Selection</Label>
                <ComboBox
                  iconClassName="wa"
                  icon={<Icon icon="arrow-down" />}
                  data={selectionData}
                  clearButton={false}
                  style={{ width: 200 }}
                  textField={"name"}
                  dataItemKey={"value"}
                  value={matterRefParameters.selection}
                  onChange={(e) => {
                    if (e.value?.value === "None") {
                      setMatterRefParameters({
                        ...matterRefParameters,
                        selection: e.value,
                        separator: { name: "None", value: "N" },
                      });
                      return;
                    }
                    setMatterRefParameters({
                      ...matterRefParameters,
                      selection: e.value,
                    });
                  }}
                />
              </div>
              <div className="ms-4 d-flex flex-column">
                <Label className="label mb-2">Separator</Label>
                <ComboBox
                  iconClassName="wa"
                  icon={<Icon icon="arrow-down" />}
                  data={separatorData}
                  clearButton={false}
                  style={{ width: 100 }}
                  textField={"name"}
                  dataItemKey={"value"}
                  value={matterRefParameters.separator}
                  onChange={(e) => {
                    if (matterRefParameters.selection?.value === "None") return;
                    setMatterRefParameters({
                      ...matterRefParameters,
                      separator: e.value,
                    });
                  }}
                />
              </div>
              <div className="ms-4 d-flex flex-column">
                <Label className="label mb-2">Number length</Label>
                <NumericTextBox
                  width={100}
                  min={2}
                  max={8}
                  value={matterRefParameters.numbersLength}
                  onChange={(e) =>
                    setMatterRefParameters({
                      ...matterRefParameters,
                      numbersLength: e.value,
                    })
                  }
                />
              </div>
            </div>
          </div>
        </div>
      </div>
      <div className={`${styles["buttons-container"]} d-flex align-items-center justify-content-end gap-2 px-4 py-3`}>
        <Button
          className="common-btn bgBtnSecondary"
          type="button"
          onClick={() => navigate(`/settings`)}
        >
          Cancel
        </Button>
        <SubmitButton
          onClick={submitHandler}
          isLoading={isSubmitting}
          disabled={!hasChanges}
        >
          Update
        </SubmitButton>
      </div>
    </div>
  );
};

export default MatterSetup;
